import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppTrSettings, AppTranslate } from "./AppTranslate";
import { AppTranslateRules } from "./AppTranslateRules";
import { AppIcons } from "./AppIcons";
import { AppChatSettingsView } from "./AppChatSettingsView";

// AppTranslateView - the MARKUP of translation's controls: in the chat header the STATE CHIP ("EN ⇄ ID", drawn only
// while this chat is being translated) and the quiet line under the header (the suggestion, the monthly cap, an
// unreadable settings file); in the "Chat settings" dialog the settings themselves (`Panel`: on/off, my language, their
// language, Save). ⚠ The personal glossary editor was removed (owner, 2026-10-14); a glossary already stored in
// translate.json is still read, kept on every save and used by the service - nothing adds to it.
// ⚠ UNTIL 2026-10-13 THE SETTINGS WERE AN INLINE PANEL (`#wac-trpanel`) UNDER THE HEADER that pushed the conversation
// down (owner: "Move the translation settings into a new chat settings dialog"). The panel's markup is the same; it has
// no heading and no Close of its own any more (the dialog's section and the dialog carry those).
//
// ⛔ EVERY PIECE OF TEXT IS ESCAPED (`kHTMLCore`) - languages and the contact's name are data.
// ⛔ NO INLINE STYLE (the CSP): classes and `app.css`; hiding is `hidden`.
// ⚠ Actions are `data-wac-tract` values the controller (`AppTranslateChip`) reads from ONE delegated listener; the
// chip itself only OPENS the dialog (`AppChatSettingsView.OpenAttr`) - it changes nothing.

export class AppTranslateView {
    public static readonly ChipId: string = "wac-trchip";
    public static readonly LineId: string = "wac-trline";
    public static readonly ActAttr: string = "data-wac-tract";
    public static readonly MyId: string = "wac-trmy";
    public static readonly TheirId: string = "wac-trtheir";
    public static readonly OnId: string = "wac-tron";
    public static readonly NoteId: string = "wac-trnote";

    /** The chip's empty place on the `.back` line. DOCUMENTED EXCEPTION: a literal. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Slots = (): string => {
        return "<span class=\"trchipbox\" id=\"" + AppTranslateView.ChipId + "\"></span>";
    };

    /** Under the chat header: the quiet line, empty and hidden until there is something.
     *  DOCUMENTED EXCEPTION: a literal. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Below = (): string => {
        return "<p class=\"trline\" id=\"" + AppTranslateView.LineId + "\" role=\"status\" hidden></p>";
    };

    /**
     * ⭐ THE STATE CHIP: the flag and "EN ⇄ ID", in the chat header, ONLY WHILE THIS CHAT IS BEING TRANSLATED - the owner
     * sees it before they send. "" when translation is off for the chat (its settings are in the dialog; nothing in the
     * header says "off"). A press opens "Chat settings" at Translation (`AppChatSettingsView.OpenAttr`); it changes nothing.
     */
    public static Chip = (S: AppTrSettings | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTranslate.Active(S) !== true) { return ""; }
            const Label: string = AppTranslateRules.ChipLabel(S);
            return "<button type=\"button\" class=\"trchip on\" " + AppChatSettingsView.OpenAttr + "=\"" + AppChatSettingsView.TrSection
                + "\" aria-haspopup=\"dialog\" aria-label=\"" + kHTMLCore.EscapeAttr("Translation is on: " + Label + ". Open chat settings")
                + "\" title=\"" + kHTMLCore.EscapeAttr(AppTranslateRules.ChipTitle(S)) + "\">" + AppIcons.Svg(AppIcons.Flag, 14)
                + "<span>" + kHTMLCore.EscapeHtml(Label) + "</span></button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateView.Chip");
            return "";
        }
    };

    /** The suggestion: its sentence and one tap - "Translate" - plus "Not now". ⛔ Nothing is switched on without the tap. */
    public static Suggest = (Line: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return kHTMLCore.EscapeHtml(Line) + " <button type=\"button\" class=\"trgo btn sm\" " + AppTranslateView.ActAttr
                + "=\"accept\">Translate</button> <button type=\"button\" class=\"trlink btn ghost sm\" " + AppTranslateView.ActAttr
                + "=\"dismiss\">Not now</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateView.Suggest");
            return "";
        }
    };

    /**
     * The translation settings for `Draft` (the dialog's Translation section). `Busy` "" or "Saving…"; `Note` a sentence
     * (`Bad` in the alarm colour).
     * ⚠ A BUSY Save IS `aria-disabled`, NEVER `disabled`: the section is redrawn while the keyboard is on that button, and
     * a disabled button cannot keep the focus - it would fall out of the section (`AppTranslateChip.Save` ignores a
     * second press while busy).
     */
    public static Panel = (Draft: AppTrSettings, Busy: string, Note: string, Bad: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Checked: string = "";
            if (Draft.On === true) { Checked = " checked"; }
            let Disabled: string = "";
            if (Busy.length > 0) { Disabled = " aria-disabled=\"true\""; }
            let NoteCls: string = "trnote";
            if (Bad === true) { NoteCls = "trnote bad"; }
            return "<label class=\"trrow\"><input type=\"checkbox\" id=\"" + AppTranslateView.OnId + "\"" + Checked + "> Translate this chat</label>"
                + "<div class=\"trlangs\">" + AppTranslateView.Select(AppTranslateView.MyId, "I read and write", Draft.MyLang)
                + AppTranslateView.Select(AppTranslateView.TheirId, "They get", Draft.TheirLang) + "</div>"
                + "<p class=\"trbtns\"><button type=\"button\" class=\"trgo btn sm\" " + AppTranslateView.ActAttr + "=\"save\"" + Disabled + ">"
                + kHTMLCore.EscapeHtml(AppTranslateView.SaveWords(Busy)) + "</button></p>"
                + "<p class=\"" + NoteCls + "\" id=\"" + AppTranslateView.NoteId + "\" role=\"status\">" + kHTMLCore.EscapeHtml(Note) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateView.Panel");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: picks the button's words. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static SaveWords = (Busy: string): string => {
        if (Busy.length > 0) { return Busy; }
        return "Save";
    };

    /** One language picker: the list (sorted by name), plus the saved code when the list does not name it. */
    public static Select = (Id: string, Label: string, Value: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<label class=\"trsel\"><span>" + kHTMLCore.EscapeHtml(Label) + "</span><select id=\""
                + kHTMLCore.EscapeAttr(Id) + "\"><option value=\"\">Choose…</option>"];
            let Listed: boolean = false;
            for (const One of AppTranslate.Languages) {
                let Sel: string = "";
                if (One[0] === Value) { Sel = " selected"; Listed = true; }
                Out.push("<option value=\"" + kHTMLCore.EscapeAttr(One[0]) + "\"" + Sel + ">" + kHTMLCore.EscapeHtml(One[1]) + "</option>");
            }
            if (Listed === false && AppTranslate.LangOk(Value) === true) {
                Out.push("<option value=\"" + kHTMLCore.EscapeAttr(Value) + "\" selected>" + kHTMLCore.EscapeHtml(AppTranslate.NameOf(Value)) + "</option>");
            }
            Out.push("</select></label>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateView.Select");
            return "";
        }
    };

    /** The sentence for a settings file that could not be read (never written over). */
    public static readonly UnreadableWhy: string = "This chat's translation settings (translate.json in its Dropbox folder)"
        + " could not be read, so nothing is translated. Restore an earlier version of the file in Dropbox, or delete it"
        + " to start again.";

    /** DOCUMENTED EXCEPTION: escapes one sentence. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Text = (Line: string): string => {
        return kHTMLCore.EscapeHtml(nz(Line));
    };
}
